import React, { useState } from "react";
import { Row, Col, Steps, Radio, Result } from "antd";
import styled, { keyframes } from "styled-components";
import ModelWrapper from "../../../modal/ModelWrapper";
import {
  CustomButton,
  CustomSecondaryButton,
} from "../../physician_page/styled";
import {
  LikeOutlined,
  CheckOutlined,
  HeartOutlined,
  StarOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { notify } from "../../../../utils/helper";
import { LuBrain } from "react-icons/lu";
import { RiSurveyLine } from "react-icons/ri";
import journeyService from "../../../../services/journey";

const { Step } = Steps;

const scaleIn = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const SuccessWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5px;
  // min-height: 400px;
  animation: ${scaleIn} 0.4s ease-out forwards;
  text-align: center;

  .success-icon {
    font-size: 72px;
    color: #52c41a;
    margin-bottom: 10px;
    filter: drop-shadow(0 4px 12px rgba(82, 196, 26, 0.3));

    @media screen and (max-width: 480px) {
      font-size: 64px;
    }
  }

  .thank-you-title {
    font-size: 28px;
    font-weight: 700;
    color: var(--primary-text-color);
    margin-bottom: 12px;

    @media screen and (max-width: 480px) {
      font-size: 24px;
    }
  }

  .thank-you-desc {
    font-size: 16px;
    color: #64748b;
    margin-bottom: 20px;
    max-width: 400px;
    line-height: 1.5;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .main-title {
    font-size: 20px;
    font-weight: 600;
    color: var(--primary-text-color);
    margin: 0;
  }

  .sub-title {
    font-size: 14px;
    font-weight: 500;
    color: #64748b;
    margin: 0;
  }
`;

const ContentWrapper = styled.div`
  padding: 10px 10px;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  // justify-content: space-between;

  .ant-steps-item-process > .ant-steps-item-container > .ant-steps-item-icon {
    background: #8a3ef4;
    border-color: #8a3ef4;
  }
  .ant-steps-small .ant-steps-item-icon {
    margin: 0;
  }
  .ant-steps-item-finish .ant-steps-item-icon {
    border-color: #8a3ef4;
  }
  .ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
    color: #8a3ef4;
  }
  .ant-steps-item-finish
    > .ant-steps-item-container
    > .ant-steps-item-content
    > .ant-steps-item-title:after {
    background-color: #8a3ef4;
  }

  .slide-container {
    animation-duration: 0.28s;
    animation-fill-mode: both;
  }

  @keyframes slideFromRight {
    from {
      opacity: 0;
      transform: translateX(35px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideFromLeft {
    from {
      opacity: 0;
      transform: translateX(-35px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .slide-in-next {
    animation-name: slideFromRight;
  }

  .slide-in-prev {
    animation-name: slideFromLeft;
  }

  .question-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 18px;
  }

  .icon-wrap {
    width: 38px;
    height: 38px;
    min-width: 38px;
    background: #faf5ff;
    color: var(--primary-color, #8a3ef4);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    padding: 6px;
  }

  h3 {
    margin: 0 !important;
    line-height: 1.4;
  }
}
`;

const QuestionText = styled.h3`
  font-weight: 600;
  font-size: 17px;
  color: var(--primary-text-color);
  line-height: 1.5;

  @media screen and (max-width: 480px) {
    font-size: 16px;
  }
`;

const OptionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
`;

const OptionItem = styled.div`
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
`;

const ConfirmationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0px;
  text-align: center;
  min-height: 235px;

  .confirm-title {
    font-size: 24px;
    font-weight: 700;
    color: var(--primary-text-color);
    margin-bottom: 5px;

    @media screen and (max-width: 480px) {
      font-size: 20px;
    }
  }

  .confirm-desc {
    font-size: 16px;
    color: #64748b;
    margin-bottom: 20px;
    max-width: 450px;
    line-height: 1.6;
  }

  .action-buttons {
    display: flex;
    gap: 16px;
  }
`;

const questions = [
  {
    icon: <LuBrain />,
    text: "After watching the video, how confident do you feel in understanding your upcoming procedure or care instructions?",
    options: [
      "Very Confident",
      "Confident",
      "Somewhat Confident",
      "Not Very Confident",
      "Not Confident at All",
    ],
  },
  {
    icon: <LikeOutlined />,
    text: "How helpful was the video in preparing you compared to written instructions or verbal explanations?",
    options: [
      "Much More Helpful",
      "More Helpful",
      "About the Same",
      "Somewhat Less Helpful",
      "Less Helpful",
    ],
  },
  {
    icon: <CheckOutlined />,
    text: "Did the video make you more likely to follow through with your procedure, appointment, or care plan?",
    options: ["Yes, Definitely", "Somewhat", "Not at All"],
  },
  {
    icon: <HeartOutlined />,
    text: "Did watching the video reduce any anxiety or stress you had about your care?",
    options: [
      "Significantly Reduced",
      "Somewhat Reduced",
      "No Change",
      "Slightly Increased",
      "Increased Anxiety",
    ],
  },
  {
    icon: <StarOutlined />,
    text: "Would you recommend that other patients receive videos like this before their appointments?",
    options: ["Yes", "Not Sure", "No"],
  },
];

const PatientFeedbackModal = ({
  isOpen,
  setIsOpen,
  videoData,
  journeyData,
}) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(questions.length).fill(null));
  const [animDir, setAnimDir] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const handleNext = () => {
    setAnimDir("next");
    setTimeout(() => setCurrent((prev) => prev + 1), 0);
  };

  const handlePrev = () => {
    setAnimDir("prev");
    setTimeout(() => setCurrent((prev) => prev - 1), 0);
  };

  const handleSkip = () => {
    setAnimDir("next");
    setTimeout(() => setCurrent((prev) => prev + 1), 0);
    setAnswers(answers.map((item, index) => index === current ? null : item));
  };

  const getRatingScale = (optionCount) => {
    if (optionCount === 5) return [5, 4, 3, 2, 1];
    if (optionCount === 3) return [5, 3, 1];
    if (optionCount === 4) return [5, 4, 2, 1];
    if (optionCount === 2) return [5, 1];
    return [];
  };

  const handleSubmit = () => {
    const assignId = journeyData?.id || journeyData?._id;
    const survey = questions
      .map((q, index) => {
        if (!answers[index]) {
          return null; // Skip unanswered questions or handle as needed
        }

        const selectedOptionIndex = q.options.indexOf(answers[index]); // 0,1,2...

        const ratingScale = getRatingScale(q.options.length);
        const rating = ratingScale[selectedOptionIndex]; // pick correct value

        return {
          questionNumber: index + 1,
          question: q.text,
          answer: answers[index],
          selectedOption: selectedOptionIndex + 1,
          rating,
        };
      })
      .filter((item) => item !== null);

    if (survey.length === 0) {
      notify("warn", "Please answer at least one question");
      return;
    }
    try {
      const res = journeyService.submitFeedback(assignId, {
        videoId: videoData?.video?.id || videoData?.video?._id,
        stepIndex: videoData?.order,
        survey: survey,
      });
    } catch (error) {
      notify("error", error.message);
    }
    setIsSubmitted(true);
  };
  const handleClose = () => {
    setIsOpen(false);
    // Reset state after a delay to ensure animation doesn't look glitchy if reopened immediately
    setTimeout(() => {
      setCurrent(0);
      setAnswers(Array(questions.length).fill(null));
      setIsSubmitted(false);
      setHasStarted(false);
    }, 300);
  };

  return (
    <ModelWrapper
      show={isOpen}
      setShow={handleClose}
      title={
        !isSubmitted && hasStarted ? (
          <>
            <ModalHeader>
              <div className="main-title">Give us your feedback</div>
              <div className="sub-title">
                What was your experience while watching this video?
              </div>
            </ModalHeader>
          </>
        ) : null
      }
      footer={null}
      width={isSubmitted || !hasStarted ? 500 : 700}
      centered
    >
      {!hasStarted && !isSubmitted ? (
        <ConfirmationWrapper>
          <RiSurveyLine
            style={{ fontSize: 64, color: "#8a3ef4", marginBottom: 15 }}
          />
          <div className="confirm-title">Please complete a brief survey</div>
          <div className="confirm-desc">
            We appreciate your feedback to help us improve the experience for
            you and other patients.
          </div>
          <div className="action-buttons">
            <CustomSecondaryButton
              onClick={handleClose}
              style={{ minWidth: 140, height: 44 }}
            >
              Skip
            </CustomSecondaryButton>
            <CustomButton
              type="primary"
              onClick={() => setHasStarted(true)}
              style={{ minWidth: 140, height: 44 }}
            >
              Begin Survey
            </CustomButton>
          </div>
        </ConfirmationWrapper>
      ) : isSubmitted ? (
        <SuccessWrapper>
          <CheckCircleFilled className="success-icon" />
          <div className="thank-you-title">Thank You!</div>
          <div className="thank-you-desc">
            Your feedback helps us improve the experience for you and other
            patients.
          </div>
          <CustomButton
            type="primary"
            onClick={handleClose}
            style={{ minWidth: 120 }}
          >
            Close
          </CustomButton>
        </SuccessWrapper>
      ) : (
        <ContentWrapper>
          {/* Steps Navigation */}
          <Steps
            current={current}
            size="small"
            orientation="horizontal"
            responsive={false}
            style={{ marginBottom: 30 }}
          >
            {questions.map((_, idx) => (
              <Step key={idx} />
            ))}
          </Steps>

          <div
            key={current}
            className={`slide-container ${
              animDir === "next"
                ? "slide-in-next"
                : animDir === "prev"
                ? "slide-in-prev"
                : ""
            }`}
            onAnimationEnd={() => setAnimDir(null)}
          >
            <div className="question-header">
              <div className="icon-wrap">{questions[current].icon}</div>
              <QuestionText>{questions[current].text}</QuestionText>
            </div>

            <Radio.Group
              value={answers[current]}
              onChange={(e) => {
                const arr = [...answers];
                arr[current] = e.target.value;
                setAnswers(arr);
              }}
              style={{ marginLeft: "45px" }}
            >
              <OptionsGroup>
                {questions[current].options.map((opt, index) => (
                  <OptionItem key={index}>
                    <Radio value={opt}>{opt}</Radio>
                  </OptionItem>
                ))}
              </OptionsGroup>
            </Radio.Group>
          </div>

          <Row className="mt-5">
            <Col span={24} className="d-flex justify-end gap-12">
              {current < questions.length - 1 && (
                <CustomSecondaryButton onClick={handleSkip}>
                  Skip
                </CustomSecondaryButton>
              )} 
              {current > 0 && (
                <CustomSecondaryButton onClick={handlePrev}>
                  Previous
                </CustomSecondaryButton>
              )}
              {current < questions.length - 1 ? (
                <CustomButton type="primary" onClick={handleNext}>
                  Next
                </CustomButton>
              ) : (
                <CustomButton type="primary" onClick={handleSubmit}>
                  Submit
                </CustomButton>
              )}
            </Col>
          </Row>
        </ContentWrapper>
      )}
    </ModelWrapper>
  );
};
export default PatientFeedbackModal;
